import { CfsApi, TActiveCFSFilter } from '@/api';
import { IGetParams } from '@/api/types';
import { AssignmentStatus, CFSEvent, ICFS } from '@/models';
import {
  CFSNavigationContext,
  useCFSNavigationStore,
} from '@/store/store.cfsNavigation';

type AgencyFilterItem = {
  _id?: string;
  type?: string;
};

const REFRESHABLE_NAVIGATION_EVENTS = new Set<string>([
  CFSEvent.CREATED,
  CFSEvent.CLOSED,
  CFSEvent.REOPENED,
  CFSEvent.READY_TO_DISPATCH,
  CFSEvent.UNREADY_TO_DISPATCH,
]);

const ensureCurrentCfsId = (ids: string[], currentCfsId: string) => {
  if (!currentCfsId || ids.includes(currentCfsId)) {
    return ids;
  }

  return [currentCfsId, ...ids];
};

export const buildActiveCFSApiParams = (
  userFilter: TActiveCFSFilter,
  sort: string,
  search?: string,
): IGetParams => {
  const apiFilters: Record<string, unknown> = {};

  if (
    (userFilter.assignmentStatus as string) === 'isReadyToDispatch' ||
    userFilter.assignmentStatus === AssignmentStatus.NEW
  ) {
    apiFilters.assignmentStatus = AssignmentStatus.NEW;
  } else if (userFilter.assignmentStatus) {
    apiFilters.assignmentStatus = userFilter.assignmentStatus;
  }

  if (userFilter.severity) {
    apiFilters.severity = userFilter.severity;
  }

  if (userFilter.readBy !== undefined) {
    apiFilters.readBy = userFilter.readBy;
  }

  const params: IGetParams = {
    filter: JSON.stringify(apiFilters),
    sort,
  };

  if (search?.trim()) {
    params.search = search.trim();
  }

  return params;
};

export const filterActiveCFSResults = (
  results: ICFS[],
  userFilter: TActiveCFSFilter,
  agencies: AgencyFilterItem[],
  agencyTypeAgencyIds?: string[],
) => {
  let filteredResults = results;

  if ((userFilter.assignmentStatus as string) === 'isReadyToDispatch') {
    filteredResults = filteredResults.filter(
      (item) => item.isReadyToDispatch === true,
    );
  } else if (userFilter.assignmentStatus === AssignmentStatus.NEW) {
    filteredResults = filteredResults.filter(
      (item) => item.isReadyToDispatch !== true,
    );
  }

  if (userFilter.agencies?.length) {
    filteredResults = filteredResults.filter((item) =>
      item.assignedAgencies?.some((assigned) =>
        userFilter.agencies?.includes(assigned),
      ),
    );
  }

  if (userFilter.agencyType?.length) {
    const matchingAgencyIds =
      agencyTypeAgencyIds ??
      agencies
        .filter(
          (agency) =>
            agency._id && userFilter.agencyType?.includes(agency.type ?? ''),
        )
        .map((agency) => agency._id as string);

    if (!matchingAgencyIds.length && !agencyTypeAgencyIds && !agencies.length) {
      return filteredResults;
    }

    filteredResults = filteredResults.filter((item) =>
      item.assignedAgencies?.some((agencyId) =>
        matchingAgencyIds.includes(agencyId),
      ),
    );
  }

  return filteredResults;
};

export const resolveAgencyTypeAgencyIds = (
  userFilter: TActiveCFSFilter,
  agencies: AgencyFilterItem[],
) => {
  if (!userFilter.agencyType?.length) {
    return undefined;
  }

  return agencies
    .filter(
      (agency) =>
        agency._id && userFilter.agencyType?.includes(agency.type ?? ''),
    )
    .map((agency) => agency._id as string);
};

export const buildCFSListExtraFilters = (filters: TActiveCFSFilter) => {
  const params: Record<string, unknown> = {};

  if (filters.assignmentStatus === ('isReadyToDispatch' as AssignmentStatus)) {
    params.assignmentStatus = 'NEW';
  } else if (filters.assignmentStatus) {
    params.assignmentStatus = filters.assignmentStatus;
  }

  if (filters.severity) {
    params.severity = filters.severity;
  }

  if (filters.readBy !== undefined) {
    params.readBy = filters.readBy;
  }

  if (filters.agencies) {
    params.agencies = filters.agencies;
  }

  if (filters.agencyType?.length) {
    params.agencyTypes = filters.agencyType;
  }

  return params;
};

export const buildCFSListNavigationParams = ({
  filter,
  sort,
  filters,
}: {
  filter: string;
  sort: string;
  filters: TActiveCFSFilter;
}): IGetParams & Record<string, unknown> => {
  return {
    filter,
    sort,
    ...buildCFSListExtraFilters(filters),
  };
};

export const shouldRefreshCFSNavigationOnEvent = (eventName?: string) => {
  if (!eventName) {
    return false;
  }

  return REFRESHABLE_NAVIGATION_EVENTS.has(eventName);
};

export const refreshCFSNavigation = async ({
  currentCfsId,
  agencies,
  navigationContext,
}: {
  currentCfsId: string;
  agencies: AgencyFilterItem[];
  navigationContext?: CFSNavigationContext | null;
}) => {
  const context =
    navigationContext ?? useCFSNavigationStore.getState().navigationContext;

  if (!context) {
    return null;
  }

  if (context.sourceRoute === 'cfs-active') {
    const response = await CfsApi.activeList({
      ...context.requestParams,
      limit: 100,
    });
    const filteredResults = filterActiveCFSResults(
      response.data,
      context.userFilter,
      agencies,
      context.agencyTypeAgencyIds,
    );
    const ids = ensureCurrentCfsId(
      filteredResults
        .map((item) => item._id)
        .filter((id): id is string => Boolean(id)),
      currentCfsId,
    );

    useCFSNavigationStore
      .getState()
      .setCFSNavigationIds(ids, context.sourceRoute, context);

    return ids;
  }

  const response = await CfsApi.listIds(context.requestParams);
  const ids = ensureCurrentCfsId(response.data.ids, currentCfsId);

  useCFSNavigationStore
    .getState()
    .setCFSNavigationIds(ids, context.sourceRoute, context);

  return ids;
};
